import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import {
  Box,
  CloseIcon,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SearchIcon,
  Spinner,
  Text,
} from '@tradener/lumen';
import { debounce } from 'lodash';

import CREDIT_COUNTERPART_LIST, {
  PayloadProps as CreditCounterpartiesPayloadProps,
  ResponseProps as CreditCounterpartiesResponseProps,
} from '@graphql/query/operationLimits/credit/counterpartList';

import useColorMode from '~/hooks/useColorMode';
import useOperationLimitsCompetenceFilters from '~/pages/OperationLimits/hooks/useOperationLimitsCompetenceFilters';

export interface CounterpartyHeaderSearchProps {
  currentCounterpartyId?: string;
  currentCounterpartyName?: string;
  onSelectCounterpartyName?: (counterpartyName: string) => void;
  onClearCounterparty?: () => void;
}

function CounterpartyHeaderSearch({
  currentCounterpartyId,
  currentCounterpartyName,
  onSelectCounterpartyName,
  onClearCounterparty,
}: CounterpartyHeaderSearchProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { colorTernary } = useColorMode();
  const { queryFilters } = useOperationLimitsCompetenceFilters();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 300),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  useEffect(() => {
    const counterpartyNameFromUrl = searchParams.get('counterpartyName');

    if (counterpartyNameFromUrl) {
      setSearchValue(counterpartyNameFromUrl);

      return;
    }

    if (!currentCounterpartyId) {
      setSearchValue('');

      return;
    }

    if (currentCounterpartyName) {
      setSearchValue(currentCounterpartyName);
    }
  }, [currentCounterpartyId, currentCounterpartyName, searchParams]);

  const buildDetailUrl = (accountId = currentCounterpartyId, nextCounterpartyName?: string) => {
    const nextSearchParams = new URLSearchParams(location.search);

    if (nextCounterpartyName) {
      nextSearchParams.set('counterpartyName', nextCounterpartyName);
    } else {
      nextSearchParams.delete('counterpartyName');
    }

    const nextSearch = nextSearchParams.toString();
    const basePath = accountId ? `/limits/dashboard/${accountId}` : '/limits/dashboard';

    return `${basePath}${nextSearch ? `?${nextSearch}` : ''}`;
  };

  const { data, loading } = useQuery<CreditCounterpartiesResponseProps, CreditCounterpartiesPayloadProps>(
    CREDIT_COUNTERPART_LIST,
    {
      variables: {
        filter: {
          counterpartName: searchTerm || undefined,
          startCompetence: queryFilters.startCompetence,
          endCompetence: queryFilters.endCompetence,
        },
      },
      skip: !isOpen,
      fetchPolicy: 'cache-and-network',
    },
  );

  const counterparties = data?.creditCounterpartList || [];

  const closeSearch = () => {
    setIsOpen(false);
    window.setTimeout(() => {
      inputRef.current?.blur();
    }, 0);
  };

  const handleSelectCounterparty = (accountId: string, counterpartName: string) => {
    setSearchValue(counterpartName);
    closeSearch();
    onSelectCounterpartyName?.(counterpartName);

    if (accountId === currentCounterpartyId) {
      return;
    }

    navigate(buildDetailUrl(accountId, counterpartName));
  };

  const handleSubmitSearch = () => {
    const normalizedValue = searchValue.trim().toLowerCase();

    if (!normalizedValue) {
      if (currentCounterpartyId) {
        navigate(buildDetailUrl());
      }

      closeSearch();

      return;
    }

    const exactCounterparty = counterparties.find((item) => item.counterpartName.toLowerCase() === normalizedValue);
    const fallbackCounterparty = counterparties[0];
    const selectedCounterparty = exactCounterparty || fallbackCounterparty;

    if (!selectedCounterparty) {
      return;
    }

    handleSelectCounterparty(selectedCounterparty.accountId, selectedCounterparty.counterpartName);
  };

  const handleClear = () => {
    setSearchValue('');
    setSearchTerm('');
    closeSearch();
    onClearCounterparty?.();

    if (currentCounterpartyId) {
      navigate(buildDetailUrl());
    }
  };

  return (
    <Box position="relative" zIndex="dropdown" width="full">
      <InputGroup width="full" height="8" textStyle="body2-regular">
        <Input
          ref={inputRef}
          value={searchValue}
          bgColor="transparent"
          textStyle="body2-regular"
          borderWidth="0"
          borderColor="transparent"
          borderRadius="base"
          colorScheme="gray"
          color="gray.900"
          autoComplete="off"
          overflow="clip"
          size="sm"
          height="8"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          placeholder="Buscar contraparte"
          _placeholder={{ color: 'gray.500' }}
          _hover={{ borderColor: 'transparent' }}
          _focus={{
            bgColor: 'transparent',
            borderColor: 'transparent',
            boxShadow: 'none',
          }}
          _focusVisible={{ boxShadow: 'none' }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            window.setTimeout(() => setIsOpen(false), 120);
          }}
          onChange={(event) => {
            const value = event.target.value;

            setSearchValue(value);
            setIsOpen(true);
            debouncedSearch(value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') {
              return;
            }

            event.preventDefault();
            handleSubmitSearch();
          }}
        />
        <InputRightElement height="8" color="gray.900" right="1">
          {loading && isOpen ? (
            <Spinner size="sm" color="gray.500" />
          ) : (
            <IconButton
              aria-label="clear-counterparty-search"
              variant="ghost"
              colorScheme="gray"
              color="gray.900"
              size="sm"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (!searchValue) {
                  return;
                }

                handleClear();
              }}
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              _focusVisible={{ boxShadow: 'none' }}
            >
              {searchValue ? <CloseIcon boxSize="4.5" /> : <SearchIcon boxSize="4.5" />}
            </IconButton>
          )}
        </InputRightElement>
      </InputGroup>

      {isOpen ? (
        <Box
          position="absolute"
          top="calc(100% + .25rem)"
          left="0"
          right="0"
          bg={colorTernary('rgba(23, 23, 23, 1)', 'gray.0')}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={colorTernary('rgba(255, 255, 255, 0.12)', 'gray.100')}
          boxShadow="lg"
          maxH="18rem"
          overflowY="auto"
        >
          {loading ? (
            <Flex align="center" justify="center" p="3">
              <Text textStyle="body2-regular" color="gray.500">
                Buscando...
              </Text>
            </Flex>
          ) : null}

          {!loading && !counterparties.length ? (
            <Flex align="center" justify="center" p="3">
              <Text textStyle="body2-regular" color="gray.500">
                Nenhuma contraparte encontrada
              </Text>
            </Flex>
          ) : null}

          {!loading
            ? counterparties.map((counterparty) => (
                <Flex
                  key={counterparty.accountId}
                  direction="column"
                  gap=".5"
                  px="3"
                  py="2.5"
                  borderBottomWidth="1px"
                  borderBottomColor={colorTernary('rgba(255, 255, 255, 0.08)', 'gray.100')}
                  cursor="pointer"
                  _hover={{ bgColor: colorTernary('rgba(255, 255, 255, 0.06)', 'gray.50') }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelectCounterparty(counterparty.accountId, counterparty.counterpartName);
                  }}
                >
                  <Text
                    textStyle="body2-medium"
                    color={counterparty.accountId === currentCounterpartyId ? 'brand.500' : 'gray.900'}
                    lineHeight="1"
                  >
                    {counterparty.counterpartName}
                  </Text>
                </Flex>
              ))
            : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default CounterpartyHeaderSearch;
